import { useState, useEffect, useMemo, useCallback } from "react";
import type { IframeBlock as IframeBlockType, LeafBlock } from "../../../shared/schema";
import { useIframeToken } from "../../hooks/useIframeToken";
import { buildSandboxAttr, getHeightStyle, buildUrlWithParams } from "./iframeUtils";

interface UseIframeBlockResult {
  finalUrl: string | null;
  sandboxAttr: string;
  heightStyle: string;
  isLoading: boolean;
  showFallback: boolean;
  fallbackBlocks: LeafBlock[];
  title: string | undefined;
}

export const useIframeBlock = (block: IframeBlockType): UseIframeBlockResult => {
  const {
    url,
    height = "md",
    title,
    passToken = true,
    tokenParam = "token",
    sandbox = { allowScripts: true, allowSameOrigin: true, allowForms: true, allowPopups: false },
  } = block.props;

  // Fetch iframe token using TanStack Query hook
  const { token, isLoading, error } = useIframeToken({ enabled: passToken });

  const [hasIframeError, setHasIframeError] = useState(false);

  // Derive showFallback from token fetch errors or iframe postMessage errors
  const showFallback = useMemo(() => {
    return Boolean(error || hasIframeError);
  }, [error, hasIframeError]);

  // Listen for error postMessage from iframe (with origin validation)
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (!url) return;
      try {
        const expectedOrigin = new URL(url).origin;
        if (event.origin !== expectedOrigin) return;
      } catch {
        return;
      }

      if (event.data && typeof event.data === "object" && event.data.type === "iframe-error") {
        setHasIframeError(true);
      }
    },
    [url]
  );

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [handleMessage]);

  // Build final URL with token
  const finalUrl = useMemo(() => {
    if (!url) return null;
    if (!passToken) return url;
    if (!token) return null;
    return buildUrlWithParams(url, { [tokenParam]: token });
  }, [url, passToken, token, tokenParam]);

  // Build sandbox attribute
  const sandboxAttr = useMemo(() => buildSandboxAttr(sandbox), [sandbox]);

  // Get height CSS value
  const heightStyle = useMemo(() => getHeightStyle(height), [height]);

  return {
    finalUrl,
    sandboxAttr,
    heightStyle,
    isLoading,
    showFallback,
    fallbackBlocks: block.fallbackBlocks,
    title,
  };
};
